import { Controller } from "@hotwired/stimulus"

/**
 * Topic Ideation Controller (C7)
 *
 * Drives the universal AI ideation panel (KitWizard::TopicIdeationComponent) on
 * the first content step of every kit-template wizard. "Suggest topics" POSTs to
 * the engine's suggest_topics member action and renders the returned ideas;
 * "Draft this" POSTs a chosen topic to draft_content, which persists the filled
 * fields server-side, then reloads so the form shows the draft.
 *
 * Server contract (Ideation::SuggestTopicsService / DraftContentService):
 *   POST suggestUrl { hint }       -> { success, topics: [{ topic, brief }] } | { success:false, error }
 *   POST draftUrl   { topic, brief, hint } -> { success, reload } | { success:false, error }
 */
export default class extends Controller {
  static targets = ["hint", "suggestButton", "status", "results"]
  static values = { suggestUrl: String, draftUrl: String }

  connect() {
    this.busy = false
  }

  async suggest() {
    if (this.busy) return
    this.setBusy(true)
    this.showStatus("Thinking of topics…", "loading")

    try {
      const data = await this.post(this.suggestUrlValue, { hint: this.hintValue() })
      if (data && data.success && Array.isArray(data.topics) && data.topics.length) {
        this.renderTopics(data.topics)
        this.showStatus("", "info")
      } else {
        this.showStatus((data && data.error) || "No topics came back — try again", "error")
      }
    } catch (error) {
      this.showStatus("Network error", "error")
    } finally {
      this.setBusy(false)
    }
  }

  async draft(event) {
    if (this.busy) return
    const button = event.currentTarget
    const topic = button.dataset.topic || ""
    const brief = button.dataset.brief || ""
    if (!topic) return

    this.setBusy(true)
    button.textContent = "Drafting…"
    this.showStatus("Drafting your video…", "loading")

    try {
      const data = await this.post(this.draftUrlValue, { topic, brief, hint: this.hintValue() })
      if (data && data.success) {
        this.showStatus("Draft ready — loading…", "success")
        if (data.reload) window.location.reload()
        return
      }
      this.showStatus((data && data.error) || "Drafting failed", "error")
    } catch (error) {
      this.showStatus("Network error", "error")
    }

    button.textContent = "Draft this"
    this.setBusy(false)
  }

  renderTopics(topics) {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = ""

    topics.forEach((topic) => {
      const row = document.createElement("li")
      row.className = "wiz-ideation__topic"

      const text = document.createElement("div")
      text.className = "wiz-ideation__topic-text"
      const title = document.createElement("div")
      title.className = "wiz-ideation__topic-title"
      title.textContent = topic.topic || ""
      text.appendChild(title)
      if (topic.brief) {
        const brief = document.createElement("div")
        brief.className = "wiz-ideation__topic-brief"
        brief.textContent = topic.brief
        text.appendChild(brief)
      }

      const button = document.createElement("button")
      button.type = "button"
      button.className = "wiz-btn wiz-btn--primary wiz-ideation__draft"
      button.textContent = "Draft this"
      button.dataset.topic = topic.topic || ""
      button.dataset.brief = topic.brief || ""
      button.dataset.action = "topic-ideation#draft"

      row.appendChild(text)
      row.appendChild(button)
      this.resultsTarget.appendChild(row)
    })

    this.resultsTarget.hidden = false
  }

  hintValue() {
    return this.hasHintTarget ? this.hintTarget.value.trim() : ""
  }

  setBusy(busy) {
    this.busy = busy
    if (this.hasSuggestButtonTarget) this.suggestButtonTarget.disabled = busy
  }

  async post(url, body) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    })
    return response.json()
  }

  showStatus(message, type = "info") {
    if (!this.hasStatusTarget) return
    const colors = {
      loading: "#6366f1",
      success: "#16a34a",
      error: "#dc2626",
      info: "#6b7280",
    }
    this.statusTarget.textContent = message
    this.statusTarget.style.color = colors[type] || colors.info
  }
}
